import React, { useState, useEffect, useRef } from 'react';
import { Compass, ArrowRight, ArrowLeft, X, Sparkles, CheckCircle2, MousePointerClick } from 'lucide-react';

export interface TourStep {
  targetSelector: string;
  title: string;
  instruction: string;
  description: string;
  badge: string;
  actionText: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    targetSelector: '.sport-filters .sport-chip, .sport-filters',
    title: '1. Filter by Sport in Real Time',
    instruction: '👆 Click on any Sport chip (Cricket, Football, Badminton) to test live filtering!',
    description: 'Notice how the match list and turf radar instantly update to show available slots for that sport.',
    badge: 'STEP 1: SPORTS DNA',
    actionText: 'Click any sport above',
  },
  {
    targetSelector: '.games-section .game-card, .games-section',
    title: '2. Inspect a Live Match Lobby',
    instruction: '👆 Click on any Match Card to view roster slots & turf info!',
    description: 'Check team line-ups, skill levels, entry fees, and spot matches that have a "Need 1 Player" badge.',
    badge: 'STEP 2: MATCH LOBBIES',
    actionText: 'Click a match card',
  },
  {
    targetSelector: '.map-section, .map-container',
    title: '3. Explore the Live Turf Map',
    instruction: '👆 Click on the Map to interact with nearby turf venues!',
    description: 'Every pin is an active turf venue with real-time pickup slots happening in your neighborhood.',
    badge: 'STEP 3: LIVE RADAR',
    actionText: 'Click anywhere on map',
  },
  {
    targetSelector: '.location-badge, .location-btn',
    title: '4. Set Your Neighborhood Hub',
    instruction: '👆 Click your Location Badge to set your turf radius!',
    description: 'KhelDrop only matches you within your preferred playing radius (2km to 25km) to keep pickup games local.',
    badge: 'STEP 4: LOCATION HUB',
    actionText: 'Click location badge',
  },
  {
    targetSelector: '.bottom-nav__create, #tour-nav-create, button.join-btn',
    title: '5. Host a Match in 30 Seconds',
    instruction: '👆 Click the "+" Create button to see how hosting works!',
    description: 'Short on players for your box cricket or turf game? Host a game and KhelDrop broadcasts it to nearby athletes.',
    badge: 'STEP 5: HOSTING',
    actionText: 'Click Create Match (+)',
  },
];

export default function InteractiveAppTour() {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [clickedSuccess, setClickedSuccess] = useState<boolean>(false);
  const currentElementRef = useRef<HTMLElement | null>(null);

  // Open tour on custom event or when query param `tour=true`
  useEffect(() => {
    const handleStartTour = () => {
      setCurrentStepIndex(0);
      setClickedSuccess(false);
      setIsActive(true);
    };

    window.addEventListener('kheldrop_start_tour', handleStartTour);

    if (typeof window !== 'undefined' && window.location.search.includes('tour=true')) {
      handleStartTour();
      const newUrl = window.location.pathname;
      window.history.replaceState(null, '', newUrl);
    }

    return () => window.removeEventListener('kheldrop_start_tour', handleStartTour);
  }, []);

  // Attach click listener to the real active element
  useEffect(() => {
    if (!isActive) return;

    // Clean up previous target element styling
    if (currentElementRef.current) {
      currentElementRef.current.classList.remove('tour-clickable-target');
    }

    const step = TOUR_STEPS[currentStepIndex];
    if (!step) return;

    let targetEl: HTMLElement | null = null;
    const selectors = step.targetSelector.split(',').map((s) => s.trim());
    for (const sel of selectors) {
      const el = document.querySelector(sel) as HTMLElement;
      if (el) {
        targetEl = el;
        break;
      }
    }

    if (targetEl) {
      currentElementRef.current = targetEl;
      targetEl.classList.add('tour-clickable-target');
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Click listener for the real feature
      const handleFeatureClick = () => {
        setClickedSuccess(true);
        setTimeout(() => {
          setClickedSuccess(false);
          if (currentStepIndex < TOUR_STEPS.length - 1) {
            setCurrentStepIndex((prev) => prev + 1);
          } else {
            handleEndTour();
          }
        }, 600);
      };

      targetEl.addEventListener('click', handleFeatureClick, { once: true });

      return () => {
        if (targetEl) {
          targetEl.classList.remove('tour-clickable-target');
          targetEl.removeEventListener('click', handleFeatureClick);
        }
      };
    }
  }, [isActive, currentStepIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleEndTour();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, currentStepIndex]);

  const handleNext = () => {
    if (currentElementRef.current) {
      currentElementRef.current.classList.remove('tour-clickable-target');
    }
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      handleEndTour();
    }
  };

  const handlePrev = () => {
    if (currentElementRef.current) {
      currentElementRef.current.classList.remove('tour-clickable-target');
    }
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleEndTour = () => {
    if (currentElementRef.current) {
      currentElementRef.current.classList.remove('tour-clickable-target');
    }
    setIsActive(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_tour_completed', 'true');
      window.dispatchEvent(new CustomEvent('kheldrop_tour_finished'));
    }
  };

  if (!isActive) return null;

  const currentStep = TOUR_STEPS[currentStepIndex];
  const isFinalStep = currentStepIndex === TOUR_STEPS.length - 1;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999998,
        pointerEvents: 'none',
      }}
    >
      {/* Dimmed backdrop - ignores clicks on the highlighted feature */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(5, 5, 10, 0.65)',
          backdropFilter: 'blur(2px)',
          pointerEvents: 'auto',
          transition: 'all 0.25s ease',
        }}
        onClick={handleEndTour}
      />

      {/* Floating Interactive Guide Card (Interactive Pointer) */}
      <div
        style={{
          position: 'fixed',
          bottom: currentStepIndex === 4 ? 'auto' : '24px',
          top: currentStepIndex === 4 ? '24px' : 'auto',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '92%',
          maxWidth: '460px',
          background: '#09090b',
          color: '#ffffff',
          borderRadius: '24px',
          padding: '22px 24px',
          border: '2px solid #a855f7',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9), 0 0 35px rgba(124, 58, 237, 0.4)',
          zIndex: 1000006,
          pointerEvents: 'auto',
          fontFamily: 'Inter, system-ui, sans-serif',
          animation: 'fadeIn 0.2s ease-out',
        }}
      >
        {/* Step Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(124, 58, 237, 0.25)',
              border: '1px solid rgba(168, 85, 247, 0.5)',
              color: '#c084fc',
              padding: '4px 10px',
              borderRadius: '999px',
              fontSize: '11px',
              fontWeight: 800,
              letterSpacing: '0.04em',
            }}
          >
            <Compass size={13} />
            {currentStep.badge}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '12px', color: '#a1a1aa', fontWeight: 800 }}>
              {currentStepIndex + 1} / {TOUR_STEPS.length}
            </span>
            <button
              onClick={handleEndTour}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#71717a',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
              }}
              title="Close tour"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Title & Interactive Action Prompt */}
        <h3
          style={{
            fontSize: '17px',
            fontWeight: 900,
            margin: '0 0 8px',
            color: '#ffffff',
            letterSpacing: '-0.02em',
          }}
        >
          {currentStep.title}
        </h3>

        {/* Interactive Click Instruction Callout */}
        <div
          style={{
            background: clickedSuccess ? 'rgba(16, 185, 129, 0.2)' : 'rgba(124, 58, 237, 0.15)',
            border: clickedSuccess ? '1.5px solid #10b981' : '1.5px solid rgba(168, 85, 247, 0.5)',
            borderRadius: '12px',
            padding: '10px 14px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: clickedSuccess ? '#34d399' : '#e9d5ff',
            fontWeight: 800,
            fontSize: '13px',
            transition: 'all 0.2s ease',
          }}
        >
          {clickedSuccess ? (
            <>
              <CheckCircle2 size={18} color="#10b981" />
              <span>Great job! Feature clicked. Advancing...</span>
            </>
          ) : (
            <>
              <MousePointerClick size={18} color="#c084fc" />
              <span>{currentStep.instruction}</span>
            </>
          )}
        </div>

        <p
          style={{
            fontSize: '12.5px',
            color: '#a1a1aa',
            lineHeight: 1.45,
            margin: '0 0 16px',
          }}
        >
          {currentStep.description}
        </p>

        {/* Progress Bar Dots */}
        <div style={{ display: 'flex', gap: '5px', marginBottom: '16px' }}>
          {TOUR_STEPS.map((_, idx) => (
            <div
              key={idx}
              style={{
                height: '4px',
                flex: 1,
                borderRadius: '999px',
                background: idx === currentStepIndex ? '#a855f7' : idx < currentStepIndex ? '#7c3aed' : 'rgba(255, 255, 255, 0.15)',
                transition: 'all 0.25s ease',
              }}
            />
          ))}
        </div>

        {/* Footer Navigation Buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {currentStepIndex > 0 ? (
            <button
              type="button"
              onClick={handlePrev}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#e4e4e7',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '999px',
                padding: '8px 16px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <ArrowLeft size={13} /> Back
            </button>
          ) : (
            <button
              type="button"
              onClick={handleEndTour}
              style={{
                background: 'transparent',
                color: '#71717a',
                border: 'none',
                padding: '8px 10px',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Skip
            </button>
          )}

          <button
            type="button"
            onClick={handleNext}
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '999px',
              padding: '9px 22px',
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.5)',
            }}
          >
            {isFinalStep ? (
              <>
                <CheckCircle2 size={14} />
                <span>Finish & Play</span>
              </>
            ) : (
              <>
                <span>Next Feature</span>
                <ArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
